import React, { useState, useEffect, useRef, Suspense } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useStepNavigator } from '../../../../ui/StepNavigator'
import useSubmitForm from 'hooks/useSubmitForm'
import { updateFedexEulaAcceptedDate } from 'reducers/modules/app'

const ReactPDF = import(/* webpackChunkName: "react-pdf" */ 'react-pdf')

const Document = React.lazy(() =>
    ReactPDF.then((module) => ({ default: module.Document }))
)

const Page = React.lazy(() =>
    ReactPDF.then((module) => ({ default: module.Page }))
)

export const FedexEulaStep = () => {
    const { nextStep } = useStepNavigator()
    const dispatch = useDispatch()
    const eulaAcceptedDate = useSelector(
        ({ app: { user_settings: { fedex_eula_accepted_date } = {} } }: any) =>
            fedex_eula_accepted_date
    )
    const sentinelRef = useRef<HTMLDivElement>(null)
    const [canSubmit, setCanSubmit] = useState(false)
    const [agreed, setAgreed] = useState(false)
    const [numPages, setNumPages] = useState(0)
    const { submit, loading, error } = useSubmitForm(
        window.Routes.ratesmgr_user_settings_accept_fedex_eula_path()
    )

    useEffect(() => {
        if (eulaAcceptedDate) {
            nextStep()
        }
    }, [eulaAcceptedDate])

    useEffect(() => {
        ReactPDF.then((module) => {
            module.pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${module.pdfjs.version}/pdf.worker.min.js`
        })
    }, [])

    useEffect(() => {
        const dialog = document.querySelector('.modal-dialog')
        if (dialog) {
            dialog.classList.add('modal-lg')
            return () => dialog.classList.remove('modal-lg')
        }
    }, [])

    useEffect(() => {
        if (!sentinelRef.current || numPages === 0) return

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setCanSubmit(true)
                    observer.disconnect()
                }
            },
            { threshold: 0.1 }
        )

        observer.observe(sentinelRef.current)

        return () => observer.disconnect()
    }, [numPages])

    const handleAccept = async () => {
        const result = await submit()
        if (result) {
            dispatch(updateFedexEulaAcceptedDate())
            nextStep()
        }
    }

    if (eulaAcceptedDate) {
        return null
    }

    return (
        <div data-testid="fedex-eula-step">
            <h3>FedEx End User License Agreement</h3>
            <p>
                Please read and accept the FedEx End User License Agreement to
                proceed with account registration.
            </p>

            {error && <div className="alert alert-danger">{error}</div>}

            <div
                style={{ border: '1px solid #ddd', marginBottom: '16px' }}
                data-testid="eula-scroll-container"
            >
                <Suspense fallback={<div style={{ padding: '16px' }}>Loading EULA...</div>}>
                    <Document
                        file="/pdf/fedex_eula_distributed_technology.pdf"
                        onLoadSuccess={({ numPages: pages }) => setNumPages(pages)}
                    >
                        {Array.from(new Array(numPages), (_, index) => (
                            <Page
                                key={`page_${index + 1}`}
                                pageNumber={index + 1}
                            />
                        ))}
                    </Document>
                </Suspense>
                <div ref={sentinelRef} style={{ height: '1px' }} />
            </div>

            <div className="info-msg" style={{ marginBottom: '16px' }}>
                <label
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        cursor: canSubmit ? 'pointer' : 'default',
                    }}
                >
                    <input
                        type="checkbox"
                        checked={agreed}
                        onChange={(e) => setAgreed(e.target.checked)}
                        disabled={!canSubmit}
                        data-testid="eula-checkbox"
                    />
                    <span>
                        I have read and agree to the FedEx End User License Agreement
                    </span>
                </label>
            </div>

            <button
                className="btn btn-primary"
                type="button"
                onClick={handleAccept}
                disabled={!canSubmit || !agreed || loading}
                data-testid="eula-accept-button"
            >
                I accept the terms of FedEx EULA to start shipping
            </button>
        </div>
    )
}
